/**
 * Top-level auth experiences on the Login page.
 * Multi-step screens within a use case (SSO email → password, AUP modal, MFA, etc.)
 * stay as local UI state and are intentionally not separate routes.
 */
export type AuthView =
  | 'sign-up'
  | 'sign-in'
  | 'forgot-password'
  | 'reset-password'
  | 'confirm-email';

const VIEW_PATHS: Record<AuthView, string> = {
  'sign-up': '/sign-up',
  'sign-in': '/sign-in',
  'forgot-password': '/forgot-password',
  'reset-password': '/reset-password',
  'confirm-email': '/confirm-email',
};

/** Canonical path + legacy aliases (landing page / older links). */
const PATH_TO_VIEW: Record<string, AuthView> = {
  '/': 'sign-in',
  '/sign-up': 'sign-up',
  '/signup': 'sign-up',
  '/auth/signup': 'sign-up',
  '/sign-in': 'sign-in',
  '/login': 'sign-in',
  '/auth/login': 'sign-in',
  '/forgot-password': 'forgot-password',
  '/reset-password': 'reset-password',
  '/confirm-email': 'confirm-email',
};

export const AUTH_ROUTE_PATHS = [
  '/sign-up',
  '/sign-in',
  '/login',
  '/signup',
  '/auth/login',
  '/auth/signup',
  '/forgot-password',
  '/reset-password',
  '/confirm-email',
] as const;

/** Query param carrying the post-auth return URL (path + query), e.g. /azure/landing?token=… */
export const AUTH_REDIRECT_PARAM = 'redirect';

/** sessionStorage backup for the same return URL (Strict Mode / tab refresh). */
export const AUTH_RETURN_KEY = 'simply-send-auth-return';

export function normalizePathname(pathname: string): string {
  if (!pathname || pathname === '/') return '/';
  return pathname.replace(/\/+$/, '') || '/';
}

export function pathToAuthView(pathname: string): AuthView {
  const path = normalizePathname(pathname);
  // Explicit auth URLs (and bare `/`) map to a view; other paths (deep links
  // while logged out, e.g. /azure/landing) default to sign-in.
  return PATH_TO_VIEW[path] ?? 'sign-in';
}

export function authViewToPath(view: AuthView): string {
  return VIEW_PATHS[view];
}

/** True when the URL is an auth entry path that should canonicalize to a view path. */
export function isAuthEntryPath(pathname: string): boolean {
  const path = normalizePathname(pathname);
  return path in PATH_TO_VIEW;
}

/** Auth paths a logged-in user should leave (not bare `/`, which is the app home). */
export function isPostAuthBouncePath(pathname: string): boolean {
  const path = normalizePathname(pathname);
  return (AUTH_ROUTE_PATHS as readonly string[]).includes(path);
}

/** Only allow same-app relative paths (blocks protocol-relative //evil.com). */
export function sanitizeAuthRedirect(value: string | null | undefined): string | null {
  if (!value) return null;
  let decoded = value;
  try {
    decoded = decodeURIComponent(value);
  } catch {
    decoded = value;
  }
  if (!decoded.startsWith('/') || decoded.startsWith('//')) return null;
  return decoded;
}

export function rememberAuthReturn(pathname: string, search = ''): void {
  if (typeof window === 'undefined') return;
  if (isAuthEntryPath(pathname)) return;
  const full = `${normalizePathname(pathname)}${search || ''}`;
  if (!full || full === '/') return;
  window.sessionStorage.setItem(AUTH_RETURN_KEY, full);
}

export function rememberAuthReturnUrl(returnUrl: string): void {
  if (typeof window === 'undefined') return;
  const safe = sanitizeAuthRedirect(returnUrl);
  if (!safe || safe === '/') return;
  window.sessionStorage.setItem(AUTH_RETURN_KEY, safe);
}

export function peekAuthReturn(): string | null {
  if (typeof window === 'undefined') return null;
  return sanitizeAuthRedirect(window.sessionStorage.getItem(AUTH_RETURN_KEY));
}

export function clearAuthReturn(): void {
  if (typeof window === 'undefined') return;
  window.sessionStorage.removeItem(AUTH_RETURN_KEY);
}

export function getAuthReturnFromSearch(search: string): string | null {
  const raw = search.startsWith('?') ? search.slice(1) : search;
  const params = new URLSearchParams(raw);
  return sanitizeAuthRedirect(params.get(AUTH_REDIRECT_PARAM));
}

/**
 * Build the query string for auth routes, always forwarding:
 * - redirect = original deep-link path + query (e.g. /azure/landing?token=…)
 * - ref = referral code when present
 */
export function buildAuthSearch(pathname: string, search: string): string {
  const raw = search.startsWith('?') ? search.slice(1) : search;
  const current = new URLSearchParams(raw);
  const next = new URLSearchParams();

  const ref = current.get('ref');
  if (ref) next.set('ref', ref);

  // Cognito sends provider failures to the registered root callback URL.
  // Preserve the error while canonicalizing / to /sign-in; otherwise the
  // provider-link code challenge disappears before Login can render it.
  if (isAuthEntryPath(pathname) && current.has('error')) {
    next.set('error', current.get('error') || 'invalid_request');
    const description = current.get('error_description');
    if (description) next.set('error_description', description);
  }

  let redirect = sanitizeAuthRedirect(current.get(AUTH_REDIRECT_PARAM));
  if (!redirect && !isAuthEntryPath(pathname)) {
    const deepLink = `${normalizePathname(pathname)}${search || ''}`;
    redirect = sanitizeAuthRedirect(deepLink);
  }
  if (!redirect) {
    redirect = peekAuthReturn();
  }
  if (redirect) {
    next.set(AUTH_REDIRECT_PARAM, redirect);
    rememberAuthReturnUrl(redirect);
  }

  const qs = next.toString();
  return qs ? `?${qs}` : '';
}

/** Resolve where to send the user after a successful auth on an auth route. */
export function resolvePostAuthRedirect(search: string): string {
  return getAuthReturnFromSearch(search) || peekAuthReturn() || '/';
}
